import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import type { TChurnRisk, TTenantFiltersRequest } from "./store"

/**
 * Tenant filters of the reports, as on the tenants list: signed up between
 * and supported by members.
 */
export type TTenantFilters = {
	supported_by_users?: Array<{ id: number; name: string }>
	signup_after?: Date
	signup_before?: Date
}

/**
 * The same filters in the url, in the tenants list's format.
 */
export type TTenantFiltersInQuery = {
	sps?: Array<string>
	signup_after?: string
	signup_before?: string
}

export function tenantFiltersToQuery({
	supported_by_users,
	signup_after,
	signup_before,
}: TTenantFilters): TTenantFiltersInQuery {
	const query: TTenantFiltersInQuery = {}
	if (supported_by_users?.length) {
		query.sps = supported_by_users.map((user) => `${user.id}#${user.name}`)
	}
	if (signup_after) query.signup_after = dateToQuery(signup_after)
	if (signup_before) query.signup_before = dateToQuery(signup_before)
	return query
}

export function queryToTenantFilters({
	sps,
	signup_after,
	signup_before,
}: TTenantFiltersInQuery): TTenantFilters {
	const filters: TTenantFilters = {}
	if (sps?.length) {
		filters.supported_by_users = sps.map((value) => {
			const [id, ...name] = value.split("#")
			return { id: Number(id), name: name.join("#") }
		})
	}
	if (signup_after) filters.signup_after = parseDateFromQuery(signup_after)
	if (signup_before) filters.signup_before = parseDateFromQuery(signup_before)
	return filters
}

/**
 * Whole days, from the start of the first to the end of the last, in UTC.
 */
export function tenantFiltersToRequest({
	supported_by_users,
	signup_after,
	signup_before,
}: TTenantFilters): TTenantFiltersRequest {
	return {
		supported_by_users: supported_by_users?.length
			? supported_by_users.map((user) => user.id)
			: undefined,
		signup_after: signup_after
			? dateToUTCString(startOf(signup_after, "day"))
			: null,
		signup_before: signup_before
			? dateToUTCString(endOf(signup_before, "day"))
			: null,
	}
}

/**
 * Which risk levels each "Show" option of the churn report lists.
 */
export const CHURN_RISK_VIEWS: Array<[string, string, Array<TChurnRisk>]> = [
	[
		"at_risk",
		"At risk (high + medium + inactive)",
		["high", "medium", "inactive"],
	],
	["high", "High", ["high"]],
	["medium", "Medium", ["medium"]],
	["low", "Low", ["low"]],
	["new", "New (no baseline yet)", ["new"]],
	["inactive", "Inactive (no logins)", ["inactive"]],
	["all", "All", ["high", "medium", "low", "new", "inactive"]],
]

export function churnRisksFor(view?: string): Array<TChurnRisk> {
	return (CHURN_RISK_VIEWS.find(([key]) => key === view) ||
		CHURN_RISK_VIEWS[0])[2]
}

/**
 * -0.4567 → "-46%", null → "-"
 */
export function formatChange(value: number | null): string {
	if (value === null) return "-"
	const percent = Math.round(value * 100)
	return `${percent > 0 ? "+" : ""}${percent}%`
}
